import React, { useCallback, useEffect, useState } from 'react';
import { getPartnerClaims, PartnerClaim, validateCustomer } from '../../api';
import { errorMessage, formatDateTime, Notice, NoticeBox, PageHead, StatusBadge, usePortal } from './shared';

const FILTERS = ['All', 'Claimed', 'Confirmed', 'Redeemed'] as const;

// "Bookings & Claims": redeem a customer's promo code at the door, and see recent claims.
const ClaimsPage: React.FC = () => {
  const { refreshDashboard } = usePortal();
  const [form, setForm] = useState({ mobile: '', promoCode: '' });
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [claims, setClaims] = useState<PartnerClaim[] | null>(null);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('All');

  const load = useCallback(() => getPartnerClaims().then(setClaims).catch(() => setClaims([])), []);
  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    setBusy(true);
    try {
      const result = await validateCustomer(form.mobile.trim(), form.promoCode.trim());
      const redeemed = /redeemed/i.test(result.message);
      setNotice({ severity: redeemed ? 'success' : 'error', text: result.message });
      if (redeemed) {
        setForm({ mobile: '', promoCode: '' });
        load();
        refreshDashboard();
      }
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Failed to validate promo code.') });
    } finally {
      setBusy(false);
    }
  };

  const shown = (claims ?? []).filter((c) => filter === 'All' || c.status === filter);

  return (
    <>
      <PageHead title="Bookings & Claims" subtitle="Redeem customers' offers at your venue and track every claim." />
      <div className="pp-grid2 pp-claims-grid">
        <form className="pp-panel" onSubmit={handleSubmit}>
          <h3 style={{ marginTop: 0 }}>Validate Customer</h3>
          <p className="pp-muted" style={{ marginTop: -6 }}>Enter the mobile number and promo code the customer shows you.</p>
          <div className="pp-narrow">
            <NoticeBox notice={notice} />
            <div className="pp-field">
              <label htmlFor="claim-mobile">Customer Mobile<span className="pp-req">*</span></label>
              <input id="claim-mobile" className="pp-input" required inputMode="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            </div>
            <div className="pp-field">
              <label htmlFor="claim-code">Promo Code<span className="pp-req">*</span></label>
              <input id="claim-code" className="pp-input" required value={form.promoCode} onChange={(e) => setForm({ ...form, promoCode: e.target.value.toUpperCase() })} />
            </div>
            <div><button type="submit" className="pp-btn pp-gold" disabled={busy}>{busy ? 'Checking...' : 'Verify & Redeem'}</button></div>
          </div>
        </form>

        <section className="pp-panel">
          <div className="pp-strip-head">
            <h3>Recent Claims</h3>
            <div className="pp-tabs pp-tabs-inline">
              {FILTERS.map((f) => <button key={f} className={`pp-tab pp-sm ${filter === f ? 'on' : ''}`} onClick={() => setFilter(f)}>{f}</button>)}
            </div>
          </div>
          {claims === null ? (
            <p className="pp-muted">Loading claims...</p>
          ) : shown.length === 0 ? (
            <p className="pp-muted">{claims.length === 0 ? 'No claims yet. They appear here as soon as customers claim your offers.' : 'No claims with this status.'}</p>
          ) : (
            <div className="pp-table-wrap">
              <table className="pp-table">
                <thead><tr><th>Offer</th><th>Customer</th><th>Claimed</th><th>Redeemed</th><th className="num">Status</th></tr></thead>
                <tbody>
                  {shown.map((c) => (
                    <tr key={c.id}>
                      <td>{c.offerTitle}</td>
                      <td>{c.mobile}</td>
                      <td>{formatDateTime(c.claimedAtUtc)}</td>
                      <td>{c.redeemedAtUtc ? formatDateTime(c.redeemedAtUtc) : '–'}</td>
                      <td className="num"><StatusBadge status={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="pp-hint">Showing the latest 100 claims. Customer numbers are partly hidden for privacy.</p>
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default ClaimsPage;
